import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { useArea } from "common/utils";
import { useEffect, useMemo, useState } from "react";
import ActionMomentum from "./components/ActionMomentum";
import FarmerJourneyFunnel from "./components/FarmerJourneyFunnel";
import MilkQualitySection from "./components/MilkQualitySection";
import ProgrammeFarmMap from "./components/ProgrammeFarmMap";
import StatTile from "./components/StatTile";
import {
  useBirdPieData,
  useCarcaseAvailableYears,
  useCarcaseAverages,
  useHabitatByName,
  useHighLevelMetrics,
  usePcfByYear,
  useProgrammeInfo,
} from "./hooks/useInsightsDashboardQueries";
import InsightsSection from "./InsightsSection";
import {
  BirdObservationsSection,
  getBirdContext,
  ProgrammeHabitatChart,
} from "./tabs/BiodiversityTab";

// ─── Banner config (matching ProgrammeBanner.tsx) ───────────────

const PROGRAMME_BANNERS: Record<string, { image: string; bgColor: string }> = {
  "6vczLKixKSwPkIFP3JkkGD": {
    image: "https://ruumi-dashboard-assets.ruumi.io/lidl-sustainable-beef.png",
    bgColor: "#395499",
  },
  "3UqQUHVbKY5CMBthVL5Ltq": {
    image: "https://ruumi-dashboard-assets.ruumi.io/Pattemores.png",
    bgColor: "#ffffff",
  },
  "5OPgsM2LDKgFHqhLO4Kzpv": {
    image: "https://ruumi-dashboard-assets.ruumi.io/County-Milk-2.png",
    bgColor: "#ffffff",
  },
  "4frNWjGfIiNgKS5gzM3JIQ": {
    //staging
    image: "https://ruumi-dashboard-assets.ruumi.io/Dovecote-Park.png",
    bgColor: "#ffffff",
  },
  "3Z1DiaL6bsi2XXfkUMcrQR": {
    //prod
    image: "https://ruumi-dashboard-assets.ruumi.io/Dovecote-Park.png",
    bgColor: "#ffffff",
  },
};

// ─── Styled components ───────────────────────────────────────────

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
});

// ─── Helpers ─────────────────────────────────────────────────────

function fmt(
  value: number | string | undefined | null,
  maxDecimals = 1
): string {
  if (value == null) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

function LoadingBox() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
      <CircularProgress size={32} />
    </Box>
  );
}

// ─── Metric row (reused for animal perf) ─────────────────────────

function MetricRow({
  label,
  value,
  unit,
  color,
  tooltip,
}: {
  label: string;
  value: string;
  unit: string;
  color: string;
  tooltip?: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" sx={{ color: "#71717a" }}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlinedIcon
              sx={{ fontSize: 13, color: "#a1a1aa", cursor: "help" }}
            />
          </Tooltip>
        )}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: "#18181b" }}>
        {value}
        <Typography
          component="span"
          variant="caption"
          sx={{ color: "#a1a1aa", ml: 0.5 }}
        >
          {unit}
        </Typography>
      </Typography>
    </Box>
  );
}

// ─── Carbon outcome card (PCF with year-over-year delta) ────────

function CarbonOutcomeCard({
  pcfByYear,
}: {
  pcfByYear: { year: number; value: number }[];
}) {
  const latest = pcfByYear[pcfByYear.length - 1];
  const previous =
    pcfByYear.length > 1 ? pcfByYear[pcfByYear.length - 2] : undefined;
  const deltaPct =
    latest && previous && previous.value !== 0
      ? ((latest.value - previous.value) / previous.value) * 100
      : null;
  // Lower footprint is better, so a downward trend reads as success.
  const improving = deltaPct != null && deltaPct < -0.5;
  const worsening = deltaPct != null && deltaPct > 0.5;
  const deltaColor = improving
    ? COLORS.dahlia[700]
    : worsening
    ? "#DC2626"
    : "#71717a";

  return (
    <Card sx={{ height: "100%" }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, color: "#18181b", mb: 1.5 }}
      >
        Carbon Footprint
        <Tooltip
          title="The average product carbon footprint of programme farms. A wide range of production systems and environmental conditions directly influence carbon footprint emissions, which can vary season to season."
          arrow
          placement="top"
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 13, color: "#a1a1aa", cursor: "help", ml: 0.5 }}
          />
        </Tooltip>
      </Typography>
      {latest ? (
        <>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "#18181b", lineHeight: 1.1 }}
            >
              {fmt(latest.value, 2)}
            </Typography>
            <Typography variant="caption" sx={{ color: "#a1a1aa" }}>
              kg CO₂e/kg
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}
          >
            {deltaPct != null ? (
              <>
                {improving ? (
                  <TrendingDownIcon sx={{ fontSize: 16, color: deltaColor }} />
                ) : worsening ? (
                  <TrendingUpIcon sx={{ fontSize: 16, color: deltaColor }} />
                ) : null}
                <Typography
                  variant="caption"
                  sx={{ color: deltaColor, fontWeight: 600 }}
                >
                  {deltaPct > 0 ? "+" : ""}
                  {deltaPct.toFixed(1)}% vs {previous?.year}
                </Typography>
                <Typography variant="caption" sx={{ color: "#a1a1aa" }}>
                  · {latest.year} average
                </Typography>
              </>
            ) : (
              <Typography variant="caption" sx={{ color: "#a1a1aa" }}>
                {latest.year} average — trend appears with a second year of data
              </Typography>
            )}
          </Box>
        </>
      ) : (
        <Typography variant="body2" sx={{ color: "#71717a" }}>
          Carbon footprint results will appear here once the first farm
          assessments are processed.
        </Typography>
      )}
    </Card>
  );
}

// ─── Programme Banner ───────────────────────────────────────────

function ProgrammeBannerHeader({
  guid,
  title,
}: {
  guid: string | null;
  title: string | null;
}) {
  const banner = guid ? PROGRAMME_BANNERS[guid] : null;
  return (
    <Box
      sx={{
        position: "relative",
        height: 140,
        borderRadius: `${BORDER_RADIUS.medium}px`,
        overflow: "hidden",
        mx: 3,
        mt: 1,
        mb: 2,
        ...(banner
          ? {
              backgroundImage: `url("${banner.image}")`,
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundColor: banner.bgColor,
            }
          : {
              background: `linear-gradient(135deg, ${COLORS.dahlia[900]} 0%, ${COLORS.dahlia[700]} 50%, ${COLORS.dahlia[500]} 100%)`,
            }),
      }}
    >
      {!banner && title && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 700,
              textAlign: "center",
              textShadow: "0 1px 4px rgba(0,0,0,0.2)",
            }}
          >
            {title}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Main component ─────────────────────────────────────────────

export function ProjectInsightsMetrics() {
  const { fromHa, label: areaLabel } = useArea();
  const { data: programmeInfo } = useProgrammeInfo();
  const programmeGuid =
    programmeInfo?.data?.[0]?.["HighLevelMetrics.programmeGuid"] ?? null;
  const programmeType =
    programmeInfo?.data?.[0]?.["HighLevelMetrics.programmeType"] ?? "BEEF";
  const isDairy = programmeType === "DAIRY";
  const { data: hlm, isLoading: hlmLoading } = useHighLevelMetrics();
  const { data: carcaseYears } = useCarcaseAvailableYears();
  const availableCarcaseYears = useMemo(() => {
    const years = new Set<number>();
    for (const row of carcaseYears?.data ?? []) {
      const y = row["CarcaseSummary.year"];
      if (y != null) years.add(Number(y));
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [carcaseYears?.data]);
  const [selectedCarcaseYear, setSelectedCarcaseYear] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    if (!availableCarcaseYears.length) return;
    if (
      !selectedCarcaseYear ||
      !availableCarcaseYears.includes(selectedCarcaseYear)
    ) {
      setSelectedCarcaseYear(availableCarcaseYears[0]);
    }
  }, [availableCarcaseYears, selectedCarcaseYear]);

  const { data: carcase, isLoading: carcaseLoading } =
    useCarcaseAverages(selectedCarcaseYear);
  const { data: birdPieData, isLoading: birdPieLoading } = useBirdPieData();
  const { data: habitatByName, isLoading: habitatLoading } = useHabitatByName();
  const { data: pcfByYearData } = usePcfByYear();

  const pcfByYear = useMemo(
    () =>
      (pcfByYearData?.data ?? [])
        .map((r) => ({
          year: Number(r["Emissions.year"]),
          value: Number(r["Emissions.avgValue"]),
        }))
        .filter((r) => Number.isFinite(r.year) && Number.isFinite(r.value))
        .sort((a, b) => a.year - b.year),
    [pcfByYearData?.data]
  );

  const hlmRow = hlm?.data?.[0];
  const carcaseRow = carcase?.data?.[0];
  const birdSpecies: string[] = (birdPieData?.data ?? [])
    .map((r) => r["BirdObservations.bird"])
    .filter(Boolean);

  const habitats = (habitatByName?.data ?? [])
    .filter((r) => r["Biodiversity.habitatName"])
    .map((r) => ({
      name: r["Biodiversity.habitatName"],
      group: r["Biodiversity.habitatGroup"],
      areaHa: Number(r["Biodiversity.totalAreaHa"]) || 0,
      lengthM: Number(r["Biodiversity.totalLengthM"]) || 0,
    }));

  const redListCount = birdSpecies.filter(
    (name) => getBirdContext(name).status === "Red list"
  ).length;
  const amberListCount = birdSpecies.filter(
    (name) => getBirdContext(name).status === "Amber list"
  ).length;
  const habitatGroupCount = new Set(
    habitats.map((h) => h.group).filter(Boolean)
  ).size;
  const totalAreaHa = habitats.reduce((sum, h) => sum + h.areaHa, 0);

  // Hide the whole biodiversity section when there is nothing to show, rather
  // than rendering a row of zeros.
  const hasBiodiversityData =
    redListCount > 0 ||
    amberListCount > 0 ||
    habitatGroupCount > 0 ||
    totalAreaHa > 0 ||
    birdSpecies.length > 0 ||
    habitats.length > 0;

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        minHeight: "100%",
        paddingTop: "12px",
        paddingBottom: "20px",
        overflowX: "hidden",
        // Keep the content from stretching too wide on large screens.
        maxWidth: 1000,
        mx: "auto",
      }}
    >
      {/* Programme Banner */}
      <ProgrammeBannerHeader guid={programmeGuid} title={null} />

      {/* Farmer journey funnel + programme reach */}
      <InsightsSection title="Farmer Journey">
        {hlmLoading ? (
          <LoadingBox />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FarmerJourneyFunnel />
            <ProgrammeFarmMap programmeGuid={programmeGuid} />
          </Box>
        )}
      </InsightsSection>

      {/* Action momentum: what farmers are committing to and delivering */}
      <InsightsSection title="Action Momentum">
        <ActionMomentum
          farmCount={
            Number(hlmRow?.["HighLevelMetrics.sumFarmActionPlans"]) || 0
          }
        />
      </InsightsSection>

      {/* Environmental outcomes the programme is driving */}
      <InsightsSection title="Environmental Outcomes">
        {hlmLoading ? (
          <LoadingBox />
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CarbonOutcomeCard pcfByYear={pcfByYear} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Card sx={{ height: "100%" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#18181b", mb: 1.5 }}
                >
                  Regenerative Grazing
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <MetricRow
                    label="Grazing Days"
                    value={fmt(hlmRow?.["HighLevelMetrics.sumGrazingDays"], 0)}
                    unit="days"
                    color={COLORS.bellflower[500]}
                    tooltip="The average number of days per year that animals, within the programme, spent outdoors grazing."
                  />
                  <MetricRow
                    label="Avg Recovery Time"
                    value={fmt(hlmRow?.["HighLevelMetrics.sumRecoveryTime"])}
                    unit="days"
                    color={COLORS.daisy[500]}
                    tooltip="The average number of days fields are rested between grazing events, giving grass time to recover — a core regenerative grazing practice."
                  />
                </Box>
              </Card>
            </Grid>
          </Grid>
        )}
      </InsightsSection>

      {/* Biodiversity Summary — hidden entirely when there is no data */}
      {(birdPieLoading || habitatLoading || hasBiodiversityData) && (
        <InsightsSection title="Biodiversity Summary">
          {birdPieLoading || habitatLoading ? (
            <LoadingBox />
          ) : (
            <>
              {/* Scalar metrics */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" },
                  gap: 1,
                  mb: 2,
                }}
              >
                <StatTile
                  label="Red List Species"
                  value={String(redListCount)}
                  unit="observed"
                  tooltip="The number of birds detected during the biodiversity assessment using visual surveys and the Merlin App across all programme farms that are on the Red conservation list."
                />
                <StatTile
                  label="Amber List Species"
                  value={String(amberListCount)}
                  unit="observed"
                  tooltip="The number of birds detected during the biodiversity assessment using visual surveys and the Merlin App across all programme farms that are on the Amber conservation list."
                />
                <StatTile
                  label="Habitat Groups"
                  value={String(habitatGroupCount)}
                  unit="surveyed"
                  tooltip="Each farm has been mapped and the different land uses have been categorised into the appropriate habitat types."
                />
                <StatTile
                  label="Total Area Surveyed"
                  value={fmt(fromHa(totalAreaHa))}
                  unit={areaLabel}
                />
              </Box>

              {/* Interactive habitat chart */}
              {habitatLoading ? (
                <LoadingBox />
              ) : (
                habitats.length > 0 && (
                  <ProgrammeHabitatChart habitats={habitats} />
                )
              )}

              {/* Interactive bird observations */}
              {birdPieLoading ? (
                <LoadingBox />
              ) : (
                birdSpecies.length > 0 && (
                  <BirdObservationsSection
                    species={birdSpecies}
                    subtitle={`${birdSpecies.length} species recorded across the programme`}
                  />
                )
              )}
            </>
          )}
        </InsightsSection>
      )}

      {/* Production performance — conditional on programme type */}
      {isDairy ? (
        <MilkQualitySection />
      ) : (
        <InsightsSection title="Animal Performance">
          {availableCarcaseYears.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
              <TextField
                select
                size="small"
                label="Year"
                value={selectedCarcaseYear ?? ""}
                onChange={(event) =>
                  setSelectedCarcaseYear(Number(event.target.value))
                }
                sx={{ minWidth: 120 }}
              >
                {availableCarcaseYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
          {carcaseLoading ? (
            <LoadingBox />
          ) : (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card sx={{ height: "100%" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#18181b", mb: 1.5 }}
                  >
                    Carcase Metrics
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <MetricRow
                      label="Daily Liveweight Gain"
                      value={fmt(
                        carcaseRow?.["CarcaseSummary.avgDailyLifeweightGain"],
                        2
                      )}
                      unit="kg/day"
                      color={COLORS.dahlia[500]}
                      tooltip="Average Daily Liveweight Gain (kgs) across all programme cattle. The higher an animal's daily lifetime growth, the more efficient it is, and the lower its carbon footprint per kg of beef will be."
                    />
                    <MetricRow
                      label="Avg. Slaughter Age"
                      value={fmt(
                        carcaseRow?.["CarcaseSummary.avgAgeAtSlaughter"],
                        0
                      )}
                      unit="months"
                      color={COLORS.bellflower[500]}
                      tooltip="Average Age at Slaughter (months) across all programme cattle. While reducing age at slaughter can have a positive impact on carbon emissions, this metric is closely linked to carcase weight and retail specification and so it is important that this metric is not taken in isolation. Genetic and environmental factors such as breed and nutrition will influence on age at slaughter."
                    />
                    <MetricRow
                      label="Avg. Carcase Weight"
                      value={fmt(
                        carcaseRow?.["CarcaseSummary.avgCarcaseColdWeight"],
                        0
                      )}
                      unit="kg"
                      color={COLORS.daisy[500]}
                      tooltip="Average Carcase Cold Weight (kgs) across all programme cattle. The desired carcase cold weight to meet retail specification is between 270 - 420kgs. Genetic and environmental factors such as breed or nutrition will influence carcase cold weight. This metric is also linked to age at slaughter."
                    />
                    <MetricRow
                      label="Grade Specification"
                      value={
                        carcaseRow?.[
                          "CarcaseSummary.avgCarcaseWithinDesiredGrade"
                        ] != null
                          ? `${(
                              carcaseRow[
                                "CarcaseSummary.avgCarcaseWithinDesiredGrade"
                              ] * 100
                            ).toFixed(0)}`
                          : "-"
                      }
                      unit="%"
                      color={COLORS.dahlia[300]}
                      tooltip="The average % of carcases within the desired grade specification across all programme cattle."
                    />
                  </Box>
                </Card>
              </Grid>
            </Grid>
          )}
        </InsightsSection>
      )}
    </Box>
  );
}
